/**
 * @fileoverview Componente responsável por orquestrar a experiência visual do construtor de fluxos.
 */

import React, {
  useState, useEffect, useCallback, useRef, useMemo,
} from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  IconButton, Tooltip, Switch, CircularProgress,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import {
  ArrowLeft, Save, Power, GripVertical, Check, Plus,
} from "lucide-react";
import ReactFlow, {
  Background, Controls, MiniMap,
  addEdge, useNodesState, useEdgesState,
  MarkerType, ReactFlowProvider, useReactFlow,
} from "react-flow-renderer";
import "react-flow-renderer/dist/style.css";

import api from "../../../services/api";
import openSocket from "../../../services/socket-io";
import toastError from "../../../errors/toastError";
import { toast } from "react-toastify";

import useStyles from "./styles";
import FlowNode from "./FlowNode";
import useTriageEnabled from "../../../hooks/useTriageEnabled";
import useLeaveGuard from "../../../hooks/useLeaveGuard";
import usePermission from "../../../hooks/usePermission";
import LogPanel from "./LogPanel";
import ConfigPanel from "./panels/ConfigPanel";
import PromptModal, { MemoryPromptModal } from "./panels/PromptModal";
import useWorkflowSocket from "./hooks/useWorkflowSocket";
import AgentManager from "../components/AgentManager";
import { AgentOptionsContext } from "./agentNode";
import {
  SIDEBAR_CATEGORIES, NODE_TYPES_CONFIG, DEFAULT_NODES,
  NODE_ICON_COLOR, NODE_ICON_CHIP,
} from "./constants";
import { upstreamNode } from "./flowGraph";

var nodeTypes = { flowNode: FlowNode };

/* ─── Inner component (needs ReactFlowProvider) ──────── */
// Constantes de largura da sidebar redimensionável
var SIDEBAR_DEFAULT_WIDTH = 320;
var SIDEBAR_MIN_WIDTH = 260;
var SIDEBAR_MAX_WIDTH = 480;
// Chave nova (v2): ignora larguras salvas antigas, garantindo o novo padrão largo.
var SIDEBAR_STORAGE_KEY = "flowBuilder:paletteWidth";

var readSidebarWidth = function () {
  try {
    var raw = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (!raw) return SIDEBAR_DEFAULT_WIDTH;
    var n = parseInt(raw, 10);
    if (isNaN(n)) return SIDEBAR_DEFAULT_WIDTH;
    return Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, n));
  } catch (e) {
    return SIDEBAR_DEFAULT_WIDTH;
  }
};

var FlowBuilderInner = function () {
  var classes = useStyles();
  var isDark = useTheme().palette.type === "dark";
  var triageEnabled = useTriageEnabled();
  // Sem flows.edit (com a regra nova ligada), o fluxo abre só para ver: sem
  // salvar, ligar, arrastar, conectar nem apagar, e o painel do nó só mostra.
  // As mudanças em si também não valem (canEditRef, para os callbacks): nada
  // deixa o fluxo com alteração por salvar que a API recusaria.
  var canEdit = usePermission().can("flows.edit", true);
  var canEditRef = useRef(canEdit);
  canEditRef.current = canEdit;
  var history = useHistory();
  var params = useParams();
  var id = params.id;
  var reactFlowWrapper = useRef(null);
  var rfInstance = useReactFlow();

  // Sidebar redimensionável
  var sidebarRef = useRef(null);
  var logPanelRef = useRef(null);
  var sidebarWidthState = useState(function () { return readSidebarWidth(); });
  var sidebarWidth = sidebarWidthState[0];
  var setSidebarWidth = sidebarWidthState[1];

  // Painel de nós (paleta) — flutuante e FECHADO por padrão (estilo n8n). Abre
  // pelo botão "+"; sem botão de fechar — fecha ao clicar fora ou com ESC.
  var paletteState = useState(false);
  var paletteOpen = paletteState[0];
  var setPaletteOpen = paletteState[1];
  var paletteRef = useRef(null);
  var paletteToggleRef = useRef(null);
  useEffect(function () {
    if (!paletteOpen) return undefined;
    var onDown = function (e) {
      if (paletteRef.current && paletteRef.current.contains(e.target)) return;
      if (paletteToggleRef.current && paletteToggleRef.current.contains(e.target)) return;
      setPaletteOpen(false);
    };
    var onKey = function (e) { if (e.key === "Escape") setPaletteOpen(false); };
    // Fase de CAPTURA: o React Flow (d3) dá stopPropagation no mousedown do
    // canvas, então um listener normal (bubble) no document nunca receberia o
    // clique na área do fluxo. Capturando, rodamos antes dele.
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("keydown", onKey);
    return function () {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [paletteOpen]);
  var draggingRef = useRef(false);
  var dragStartRef = useRef({ x: 0, w: 0 });

  var nodesState = useNodesState([]);
  var nodes = nodesState[0];
  var setNodes = nodesState[1];
  var onNodesChange = nodesState[2];

  var edgesState = useEdgesState([]);
  var edges = edgesState[0];
  var setEdges = edgesState[1];

  // Refs sempre com o estado mais recente (usadas por undo/redo e atalhos que
  // vivem em listeners com closure "antigo").
  var nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  var edgesRef = useRef(edges);
  edgesRef.current = edges;

  // Histórico de undo/redo — pilhas de snapshots { nodes, edges }.
  var undoStackRef = useRef([]);
  var redoStackRef = useRef([]);

  // Chave canônica de um estado do canvas (ignora selected/dragging etc.) —
  // usada pra comparar o estado atual com o último SALVO: undo de volta ao
  // estado salvo apaga o pontinho amarelo em vez de deixar dirty pra sempre.
  function snapshotKey(nds, eds) {
    return JSON.stringify({
      n: (nds || []).map(function (n) {
        return { id: n.id, type: n.type, position: n.position, data: n.data };
      }),
      e: (eds || []).map(function (ed) {
        return {
          id: ed.id, source: ed.source, target: ed.target,
          sourceHandle: ed.sourceHandle, targetHandle: ed.targetHandle,
        };
      }),
    });
  }
  var savedSnapshotRef = useRef("");
  // Ativo/inativo como está gravado no servidor: um salvamento recusado volta a
  // ele (antes, invertia o interruptor, mesmo quando o pedido não mudava o ativo).
  var savedActiveRef = useRef(false);

  var saveRef = useRef(null); // aponta sempre pro handleManualSave atual (Ctrl+S)
  var onEdgesChange = edgesState[2];

  var titleState = useState("");
  var title = titleState[0];
  var setTitle = titleState[1];

  var activeState = useState(false);
  var isActive = activeState[0];
  var setIsActive = activeState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];

  var savedState = useState(false);
  var saved = savedState[0];
  var setSaved = savedState[1];

  var dirtyState = useState(false);
  var dirty = dirtyState[0];
  var setDirty = dirtyState[1];
  // Sair com alterações não salvas: menu e links abrem o modal do editor;
  // voltar do navegador usa o confirm nativo; F5 e fechar a aba, o aviso do navegador.
  var guard = useLeaveGuard(dirty, "Há alterações não salvas neste fluxo. Sair sem salvar?");

  var selectedNodeState = useState(null);
  var selectedNodeId = selectedNodeState[0];
  var setSelectedNodeId = selectedNodeState[1];

  var queuesState = useState([]);
  var queues = queuesState[0];
  var setQueues = queuesState[1];

  var whatsappsState = useState([]);
  var whatsapps = whatsappsState[0];
  var setWhatsapps = whatsappsState[1];

  var leadStatusesState = useState([]);
  var leadStatuses = leadStatusesState[0];
  var setLeadStatuses = leadStatusesState[1];

  var crmBoardsState = useState([]);
  var crmBoards = crmBoardsState[0];
  var setCrmBoards = crmBoardsState[1];

  var usersState = useState([]);
  var users = usersState[0];
  var setUsers = usersState[1];

  var wooIntegrationsState = useState([]);
  var wooIntegrations = wooIntegrationsState[0];
  var setWooIntegrations = wooIntegrationsState[1];

  // Integrações Care e os status já vistos (gatilho "Status da OS"). null: não carregou.
  var orderSourcesState = useState(null);
  var orderSources = orderSourcesState[0];
  var setOrderSources = orderSourcesState[1];

  var saveTimer = useRef(null);
  var hasLoaded = useRef(false);

  var promptModalState = useState(false);
  var promptModalOpen = promptModalState[0];
  var setPromptModalOpen = promptModalState[1];

  /* ── File upload state (tool_files) ──────────── */
  var fileUploadingState = useState(false);
  var fileUploading = fileUploadingState[0];
  var setFileUploading = fileUploadingState[1];

  var pendingFileNameState = useState("");
  var pendingFileName = pendingFileNameState[0];
  var setPendingFileName = pendingFileNameState[1];

  var pendingFileState = useState(null);
  var pendingFile = pendingFileState[0];
  var setPendingFile = pendingFileState[1];

  /* ── Memory state (tool_memory) ──────────── */
  var memNameState = useState("");
  var memName = memNameState[0];
  var setMemName = memNameState[1];

  var memContentState = useState("");
  var memContent = memContentState[0];
  var setMemContent = memContentState[1];

  var memSummaryState = useState("");
  var memSummary = memSummaryState[0];
  var setMemSummary = memSummaryState[1];

  var memGeneratingState = useState(false);
  var memGenerating = memGeneratingState[0];
  var setMemGenerating = memGeneratingState[1];

  var memEditingIdState = useState(null);
  var memEditingId = memEditingIdState[0];
  var setMemEditingId = memEditingIdState[1];

  var memExpandedIdState = useState(null);
  var memExpandedId = memExpandedIdState[0];
  var setMemExpandedId = memExpandedIdState[1];

  /* ── CRM field state (tool_crm_create) ──── */
  var crmFieldNameState = useState("");
  var crmFieldName = crmFieldNameState[0];
  var setCrmFieldName = crmFieldNameState[1];

  /* ── Memory prompt modal (expand) ──────── */
  var memPromptModalState = useState(false);
  var memPromptModalOpen = memPromptModalState[0];
  var setMemPromptModalOpen = memPromptModalState[1];

  /* ── Template state (send_template) ──── */
  var bulkTemplatesState = useState([]);
  var bulkTemplates = bulkTemplatesState[0];
  var setBulkTemplates = bulkTemplatesState[1];

  var loadingTemplatesState = useState(false);
  var loadingTemplates = loadingTemplatesState[0];
  var setLoadingTemplates = loadingTemplatesState[1];

  var lastTemplateWhatsappId = useRef(null);

  /* ── Bulk send state ──────────────────────── */
  var bulkProgressState = useState(null);
  var bulkProgress = bulkProgressState[0];
  var setBulkProgress = bulkProgressState[1];

  var bulkSendingState = useState(false);
  var bulkSending = bulkSendingState[0];
  var setBulkSending = bulkSendingState[1];

  /* ── Execution socket ──────────────────────── */
  var socketData = useWorkflowSocket(id);
  var executionLogs = socketData.executionLogs;
  var logPanelOpen = socketData.logPanelOpen;
  var setLogPanelOpen = socketData.setLogPanelOpen;
  var nodeExecution = socketData.nodeExecution;

  /* ── Load queues & whatsapps ─────────────────── */
  useEffect(function () {
    api.get("/queue").then(function (res) { setQueues(res.data); }).catch(function () {});
    api.get("/whatsapp/?session=0").then(function (res) { setWhatsapps(res.data); }).catch(function () {});
    api.get("/lead-statuses").then(function (res) { setLeadStatuses(res.data); }).catch(function () {});
    api.get("/crm-boards").then(function (res) { setCrmBoards(res.data); }).catch(function () {});
    api.get("/users/list").then(function (res) { setUsers(res.data); }).catch(function () {});
  }, []);

  /* ── Agentes de IA (bloco "Agente IA") ──────────
     A lista leve (nome e foto) para escolher o agente no bloco e mostrar o nome
     dele no desenho. Recarrega quando o "Gerenciar Agentes" fecha. */
  var agentOptionsState = useState(null);
  var agentOptions = agentOptionsState[0];
  var setAgentOptions = agentOptionsState[1];
  var loadAgentOptions = useCallback(function () {
    api.get("/ai-agents/options")
      .then(function (res) { setAgentOptions(Array.isArray(res.data) ? res.data : []); })
      .catch(function (err) { setAgentOptions(function (cur) { return cur || []; }); toastError(err); });
  }, [setAgentOptions]);
  useEffect(function () { loadAgentOptions(); }, [loadAgentOptions]);

  // O "Gerenciar Agentes" aberto pelo bloco: { key, open, nodeId, agentId } (agentId
  // null = agente novo). Fechado, segue montado até abrir de novo (com outra key): o
  // ESC que fecha o modal não fecha também o painel do bloco.
  var agentManagerState = useState(null);
  var agentManager = agentManagerState[0];
  var setAgentManager = agentManagerState[1];
  var openAgentManager = function (request) {
    setAgentManager({ key: Date.now(), open: true, nodeId: request.nodeId, agentId: request.agentId });
  };

  /* ── Lazy-load integrações WooCommerce ──────────
     Só busca quando o fluxo tem pelo menos um node tool_woocommerce.
     Fluxos sem WooCommerce evitam um GET /integrations no mount. */
  var wooLoaded = useRef(false);
  useEffect(function () {
    if (wooLoaded.current) return;
    var hasWooNode = nodes.some(function (n) {
      return n.data && n.data.nodeType === "tool_woocommerce";
    });
    if (!hasWooNode) return;
    wooLoaded.current = true;
    api.get("/integrations?type=woocommerce")
      .then(function (res) { setWooIntegrations(res.data || []); })
      .catch(function () { wooLoaded.current = false; });
  }, [nodes]);

  /* ── Integrações Care (node "Status da OS") ─────
     O painel do node pede ao abrir: status novos chegam a cada aviso do Care. */
  var loadOrderSources = useCallback(function () {
    return api.get("/workflow-order-sources")
      .then(function (res) { setOrderSources((res.data && res.data.sources) || []); })
      .catch(function (err) { setOrderSources(function (cur) { return cur || []; }); toastError(err); });
  }, [setOrderSources]);

  /* ── Bulk progress socket listener ─────────── */
  useEffect(function () {
    var companyId = localStorage.getItem("companyId");
    if (!companyId || !id) return;
    var socket = openSocket();
    var handleBulkProgress = function (data) {
      if (String(data.workflowId) !== String(id)) return;
      setBulkProgress(data);
      if (data.done) {
        setBulkSending(false);
        toast.success("Disparo concluido: " + data.sent + " enviados" + (data.failed > 0 ? ", " + data.failed + " falhas" : ""));
      }
    };
    socket.on("company-" + companyId + "-bulkProgress", handleBulkProgress);

    // Listener para stats de delivered/read/clicks vindas do webhook
    var handleWorkflowStats = function (data) {
      if (String(data.workflowId) !== String(id)) return;
      setNodes(function (prevNodes) {
        return prevNodes.map(function (n) {
          if (n.id === data.templateNodeId && data.stats) {
            return {
              ...n,
              data: { ...n.data, stats: data.stats }
            };
          }
          return n;
        });
      });
    };
    socket.on("company-" + companyId + "-workflowStats", handleWorkflowStats);

    return function () {
      socket.off("company-" + companyId + "-bulkProgress", handleBulkProgress);
      socket.off("company-" + companyId + "-workflowStats", handleWorkflowStats);
    };
  }, [id]);

  /* ── Selected node data helpers ─────────────── */
  var selectedNode = nodes.find(function (n) { return n.id === selectedNodeId; });

  /* ── Load templates when send_template node selected ── */
  useEffect(function () {
    if (!selectedNode || selectedNode.data.nodeType !== "send_template") {
      lastTemplateWhatsappId.current = null;
      return;
    }
    // A conexão vem do node de cima: "Disparo em massa" ou "Status da OS".
    var sourceNode = upstreamNode(nodes, edges, selectedNode.id);
    if (!sourceNode) { setBulkTemplates([]); return; }
    var bulkNode = null;
    if (sourceNode && (sourceNode.data.nodeType === "bulk_message" || sourceNode.data.nodeType === "order_status")) {
      bulkNode = sourceNode;
    }
    if (!bulkNode || !bulkNode.data.whatsappId) {
      lastTemplateWhatsappId.current = null;
      setBulkTemplates([]);
      return;
    }
    var waId = bulkNode.data.whatsappId;
    if (lastTemplateWhatsappId.current === waId) return;
    lastTemplateWhatsappId.current = waId;
    setLoadingTemplates(true);
    api.get("/meta/templates/" + waId)
      .then(function (res) { setBulkTemplates(res.data.templates || res.data || []); })
      .catch(function () { setBulkTemplates([]); })
      .finally(function () { setLoadingTemplates(false); });
  }, [selectedNode, edges, nodes]);

  /* ── Inject execution status into nodes for visual feedback ── */
  useEffect(function () {
    setNodes(function (nds) {
      return nds.map(function (n) {
        var status = nodeExecution[n.id];
        if (status && n.data._executionStatus !== status) {
          return Object.assign({}, n, { data: Object.assign({}, n.data, { _executionStatus: status }) });
        }
        if (!status && n.data._executionStatus) {
          var clean = Object.assign({}, n.data);
          delete clean._executionStatus;
          return Object.assign({}, n, { data: clean });
        }
        return n;
      });
    });
  }, [nodeExecution, setNodes]);

  var updateNodeData = function (key, value) {
    if (!canEdit) return;
    setNodes(function (nds) {
      return nds.map(function (n) {
        if (n.id !== selectedNodeId) return n;
        var newData = Object.assign({}, n.data);
        newData[key] = value;
        return Object.assign({}, n, { data: newData });
      });
    });
    markDirty();
  };

  /* ── Load ─────────────────────────────────────── */
  useEffect(function () {
    var cancelled = false;
    var load = function () {
      api.get("/workflows/" + id)
        .then(function (res) {
          if (cancelled) return;
          var data = res.data;
          setTitle(data.title);
          setIsActive(data.isActive);
          savedActiveRef.current = !!data.isActive;

          var loadedNodes = data.nodes && data.nodes.length > 0 ? data.nodes : DEFAULT_NODES;
          setNodes(loadedNodes);
          setEdges(data.edges || []);
          savedSnapshotRef.current = snapshotKey(loadedNodes, data.edges || []);
          hasLoaded.current = true;
        })
        .catch(function (err) {
          if (!cancelled) {
            toastError(err);
            history.push("/fluxo");
          }
        })
        .finally(function () {
          if (!cancelled) setLoading(false);
        });
    };
    load();
    return function () { cancelled = true; };
  }, [id, history, setNodes, setEdges]);

  var markDirty = useCallback(function () {
    if (!hasLoaded.current || !canEditRef.current) return;
    setDirty(true);
    setSaved(false);
    // Mutação NOVA do usuário invalida o redo. (Fica aqui, e não no
    // takeSnapshot: snapshot roda em todo mousedown/duplo-clique — se ele
    // zerasse o redo, um simples clique num node após Ctrl+Z jogaria fora o
    // que acabou de ser desfeito.)
    redoStackRef.current = [];
  }, []);

  var doSave = useCallback(function (currentNodes, currentEdges, currentTitle, currentActive, onSuccess, onError) {
    setSaving(true);
    api.put("/workflows/" + id, {
      title: currentTitle,
      isActive: currentActive,
      nodes: currentNodes,
      edges: currentEdges,
    })
      .then(function () {
        savedSnapshotRef.current = snapshotKey(currentNodes, currentEdges);
        savedActiveRef.current = !!currentActive;
        setDirty(false);
        setSaved(true);
        setTimeout(function () { setSaved(false); }, 2000);
        if (typeof onSuccess === "function") onSuccess();
      })
      .catch(function (err) {
        toastError(err);
        // Recusado (ex.: fluxo ativo com o gatilho incompleto): o interruptor volta
        // ao que está gravado, e o desenho que não foi salvo continua pendente.
        setIsActive(savedActiveRef.current);
        setDirty(snapshotKey(currentNodes, currentEdges) !== savedSnapshotRef.current);
        if (typeof onError === "function") onError(err);
      })
      .finally(function () { setSaving(false); });
  }, [id]);

  /* ── "Transformar em agente" ─────────────────────
     O servidor converte o bloco no fluxo salvo; com mudanças por salvar, salva
     antes. O desenho volta do servidor (o bloco com o agente, sem as ferramentas
     que foram para ele) e vira o novo salvo: o desfazer recomeça dali. Resolve
     com o agente criado, ou null (o erro já apareceu). */
  var convertNodeToAgent = function (nodeId) {
    return new Promise(function (resolve) {
      var convert = function () {
        api.post("/workflows/" + id + "/nodes/" + encodeURIComponent(nodeId) + "/agent")
          .then(function (res) {
            var workflow = res.data.workflow;
            setNodes(workflow.nodes);
            setEdges(workflow.edges);
            savedSnapshotRef.current = snapshotKey(workflow.nodes, workflow.edges);
            undoStackRef.current = [];
            redoStackRef.current = [];
            setDirty(false);
            loadAgentOptions();
            resolve(res.data.agent);
          })
          .catch(function (err) {
            toastError(err);
            resolve(null);
          });
      };
      if (dirty) doSave(nodes, edges, title, isActive, convert, function () { resolve(null); });
      else convert();
    });
  };

  // O agente criado pelo "Criar agente" do bloco já fica escolhido nele.
  var chooseCreatedAgent = function (nodeId, agentId) {
    if (!canEditRef.current) return;
    setNodes(function (nds) {
      return nds.map(function (n) {
        if (n.id !== nodeId) return n;
        return Object.assign({}, n, { data: Object.assign({}, n.data, { agentId: agentId }) });
      });
    });
    markDirty();
  };

  /* ── Bulk send handler ──────────────────────── */
  var handleBulkSend = useCallback(function () {
    if (bulkSending) return Promise.resolve();
    setBulkSending(true);
    setBulkProgress(null);
    // Persistir os nodes ANTES de disparar — o backend lê baseIds/schedule/mapa
    // de variáveis dos nodes salvos. Só dispara se o save der certo (sem race).
    // Retorna a promise para o modal de confirmação poder aguardar/mostrar erro.
    return api.put("/workflows/" + id, {
      title: title,
      isActive: isActive,
      nodes: nodes,
      edges: edges,
    })
      .then(function () {
        savedSnapshotRef.current = snapshotKey(nodes, edges);
        setDirty(false);
        return api.post("/workflows/" + id + "/bulk-send");
      })
      .then(function (res) {
        toast.info("Disparo iniciado: " + ((res.data && res.data.total) || 0) + " contatos");
        // Libera o botão assim que o POST é aceito; o acompanhamento ao vivo fica
        // por conta do MassSendMonitor (uma execução durável pode durar dias, e o
        // evento de progresso pode chegar com done:false ao auto-pausar no limite).
        setBulkSending(false);
      })
      .catch(function (err) {
        setBulkSending(false);
        toastError(err);
      });
  }, [id, nodes, edges, title, isActive, bulkSending]);

  /* ── Undo / Redo ──────────────────────────────────
   * Save é MANUAL (sem auto-save). Snapshots são tirados ANTES de cada ação
   * destrutiva/estrutural (arrastar, conectar, soltar, apagar, editar config).
   * Aplicar undo/redo usa setNodes/setEdges direto — não passa pelos handlers
   * do react-flow, então não gera snapshot novo (sem loop). */
  var takeSnapshot = useCallback(function () {
    var snap = {
      nodes: JSON.parse(JSON.stringify(nodesRef.current || [])),
      edges: JSON.parse(JSON.stringify(edgesRef.current || [])),
    };
    var stack = undoStackRef.current;
    var last = stack[stack.length - 1];
    // Dedup: não empilha snapshot idêntico ao topo (ex.: abrir config sem editar).
    if (last && JSON.stringify(last) === JSON.stringify(snap)) return;
    stack.push(snap);
    if (stack.length > 60) stack.shift();
    // NÃO zera o redo aqui — só uma mutação real faz isso (ver markDirty).
  }, []);

  // Undo/redo NÃO usam markDirty (que zeraria o redo): o dirty vem da
  // comparação com o último estado salvo — desfazer de volta ao salvo limpa o
  // pontinho amarelo em vez de exigir um save redundante.
  var syncDirtyFromSaved = useCallback(function (nds, eds) {
    if (!hasLoaded.current) return;
    var same = snapshotKey(nds, eds) === savedSnapshotRef.current;
    setDirty(!same);
    if (!same) setSaved(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  var undo = useCallback(function () {
    if (undoStackRef.current.length === 0) return;
    var prev = undoStackRef.current.pop();
    redoStackRef.current.push({
      nodes: JSON.parse(JSON.stringify(nodesRef.current || [])),
      edges: JSON.parse(JSON.stringify(edgesRef.current || [])),
    });
    setNodes(prev.nodes);
    setEdges(prev.edges);
    setSelectedNodeId(null); // fecha config: o node pode ter mudado/sumido
    syncDirtyFromSaved(prev.nodes, prev.edges);
  }, [setNodes, setEdges, syncDirtyFromSaved]);

  var redo = useCallback(function () {
    if (redoStackRef.current.length === 0) return;
    var next = redoStackRef.current.pop();
    undoStackRef.current.push({
      nodes: JSON.parse(JSON.stringify(nodesRef.current || [])),
      edges: JSON.parse(JSON.stringify(edgesRef.current || [])),
    });
    setNodes(next.nodes);
    setEdges(next.edges);
    setSelectedNodeId(null);
    syncDirtyFromSaved(next.nodes, next.edges);
  }, [setNodes, setEdges, syncDirtyFromSaved]);

  /* ── Connect ──────────────────────────────────── */
  var onConnect = useCallback(function (params) {
    takeSnapshot();
    var isToolEdge = params.sourceHandle === "tools";
    var edgeStyle = isToolEdge
      ? { stroke: "#8b5cf6", strokeWidth: 2, strokeDasharray: "6 3" }
      : { stroke: "#5B7CFF", strokeWidth: 2 };
    var edgeColor = isToolEdge ? "#8b5cf6" : "#5B7CFF";

    setEdges(function (eds) {
      return addEdge(
        Object.assign({}, params, {
          type: "smoothstep",
          animated: true,
          markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor },
          style: edgeStyle,
        }),
        eds
      );
    });
    markDirty();
  }, [setEdges, markDirty, takeSnapshot]);

  // Snap magnético de ALINHAMENTO: ao arrastar um node, se o centro (X ou Y)
  // dele chegar a poucos px do centro de outro node, "cola" no alinhamento
  // exato. Como os handles ficam no centro do node, isso faz a conexão vertical
  // (ou horizontal) ficar REALMENTE reta — o smoothstep desenha reto quando os
  // handles estão alinhados — mesmo com nodes de larguras diferentes. Substitui
  // o snapToGrid, que alinhava o canto (inútil aqui, pois os handles são no
  // centro e as larguras diferem).
  var onNodeDrag = useCallback(function (_evt, dragged) {
    if (!dragged || !dragged.position) return;
    var SNAP = 6; // tolerância em px (coordenadas do fluxo)
    var dcx = dragged.position.x + (dragged.width || 0) / 2;
    var dcy = dragged.position.y + (dragged.height || 0) / 2;
    var bestX = null;
    var bestY = null;
    nodes.forEach(function (n) {
      if (n.id === dragged.id || !n.position) return;
      var dx = (n.position.x + (n.width || 0) / 2) - dcx;
      var dy = (n.position.y + (n.height || 0) / 2) - dcy;
      if (Math.abs(dx) <= SNAP && (bestX === null || Math.abs(dx) < Math.abs(bestX))) bestX = dx;
      if (Math.abs(dy) <= SNAP && (bestY === null || Math.abs(dy) < Math.abs(bestY))) bestY = dy;
    });
    if (bestX === null && bestY === null) return;
    setNodes(function (nds) {
      return nds.map(function (n) {
        if (n.id !== dragged.id) return n;
        return Object.assign({}, n, {
          position: {
            x: n.position.x + (bestX || 0),
            y: n.position.y + (bestY || 0),
          },
        });
      });
    });
  }, [nodes, setNodes]);

  /* ── Drag & drop ──────────────────────────────── */
  var onDragOver = useCallback(function (e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  var onDrop = useCallback(function (e) {
    e.preventDefault();
    var nodeType = e.dataTransfer.getData("application/reactflow");
    if (!nodeType || !reactFlowWrapper.current) return;
    takeSnapshot();

    var bounds = reactFlowWrapper.current.getBoundingClientRect();
    var position = rfInstance.project({
      x: e.clientX - bounds.left,
      y: e.clientY - bounds.top,
    });

    var cfg = NODE_TYPES_CONFIG.find(function (n) { return n.type === nodeType; });
    var newNode = {
      id: nodeType + "-" + Date.now(),
      type: "flowNode",
      position: position,
      data: { label: cfg ? cfg.label : nodeType, nodeType: nodeType },
    };

    setNodes(function (nds) { return nds.concat(newNode); });
    markDirty();
  }, [rfInstance, setNodes, markDirty, takeSnapshot]);

  var onDragStart = function (e, nodeType) {
    e.dataTransfer.setData("application/reactflow", nodeType);
    e.dataTransfer.effectAllowed = "move";
  };

  /* ── Add tool from "+" button ───────────────── */
  useEffect(function () {
    var handler = function (e) {
      if (!canEditRef.current) return;
      var detail = e.detail;
      var agentNode = nodes.find(function (n) { return n.id === detail.agentNodeId; });
      if (!agentNode) return;

      var toolCfg = NODE_TYPES_CONFIG.find(function (n) { return n.type === detail.toolType; });
      if (!toolCfg) return;

      var newNode = {
        id: detail.toolType + "-" + Date.now(),
        type: "flowNode",
        position: {
          x: agentNode.position.x,
          y: agentNode.position.y + 160,
        },
        data: { label: toolCfg.label, nodeType: detail.toolType },
      };

      setNodes(function (nds) { return nds.concat(newNode); });

      var newEdge = {
        id: "tools-" + Date.now(),
        source: detail.agentNodeId,
        sourceHandle: "tools",
        target: newNode.id,
        targetHandle: "input",
        type: "smoothstep",
        animated: true,
        markerEnd: { type: MarkerType.ArrowClosed, color: "#8b5cf6" },
        style: { stroke: "#8b5cf6", strokeWidth: 2, strokeDasharray: "6 3" },
      };

      setEdges(function (eds) { return eds.concat(newEdge); });
      markDirty();
    };

    window.addEventListener("flow:addTool", handler);
    return function () { window.removeEventListener("flow:addTool", handler); };
  }, [nodes, setNodes, setEdges, markDirty]);

  var handleNodesChange = useCallback(function (changes) {
    var hasRemoved = changes.some(function (c) { return c.type === "remove"; });
    if (hasRemoved) takeSnapshot(); // captura ANTES de remover (pra Ctrl+Z)
    onNodesChange(changes);
    var hasMoved = changes.some(function (c) { return c.type === "position" && c.dragging === false; });
    if (hasMoved || hasRemoved) markDirty();
  }, [onNodesChange, markDirty, takeSnapshot]);

  var handleEdgesChange = useCallback(function (changes) {
    var hasRemoved = changes.some(function (c) { return c.type === "remove"; });
    if (hasRemoved) takeSnapshot();
    onEdgesChange(changes);
    if (hasRemoved) markDirty();
  }, [onEdgesChange, markDirty, takeSnapshot]);

  var handleManualSave = function () {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    doSave(nodes, edges, title, isActive);
  };
  saveRef.current = canEdit ? handleManualSave : null; // Ctrl+S usa sempre a versão atual

  var handleSaveExit = function () {
    doSave(nodes, edges, title, isActive, guard.confirm); // navega só se salvar OK
  };

  // Atalhos: Ctrl+Z desfaz · Ctrl+Shift+Z / Ctrl+Y refaz · Ctrl+S salva.
  useEffect(function () {
    var onKey = function (e) {
      var mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      var key = (e.key || "").toLowerCase();
      if (key === "s") { e.preventDefault(); if (saveRef.current) saveRef.current(); return; }
      var t = e.target;
      var typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if (typing) return; // não sequestra o undo nativo dos campos de texto
      if (key === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if (key === "z" && e.shiftKey) { e.preventDefault(); redo(); }
      else if (key === "y") { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return function () { window.removeEventListener("keydown", onKey); };
  }, [undo, redo]);

  var handleToggleActive = function () {
    setIsActive(function (prev) { return !prev; });
    markDirty();
  };

  // 1 clique = só seleciona (seleção nativa do react-flow → dá pra apagar com
  // Backspace); 2 cliques = abre os detalhes do node. O stopPropagation evita
  // o zoom-on-doubleclick do canvas.
  var onNodeDoubleClick = useCallback(function (event, node) {
    if (event && event.stopPropagation) event.stopPropagation();
    takeSnapshot(); // snapshot pré-edição (dedup evita duplicar sem mudança)
    setSelectedNodeId(node.id);
  }, [takeSnapshot]);

  var onPaneClick = useCallback(function () {
    setSelectedNodeId(null);
  }, []);

  // Fecha o modal de config com ESC. Se houver um Dialog MUI aberto por cima
  // (confirmação de disparo, prompts), deixa ele tratar o ESC primeiro.
  useEffect(function () {
    if (!selectedNodeId) return undefined;
    var onKey = function (e) {
      if (e.key !== "Escape") return;
      if (document.querySelector(".MuiDialog-container")) return;
      setSelectedNodeId(null);
    };
    window.addEventListener("keydown", onKey);
    return function () { window.removeEventListener("keydown", onKey); };
  }, [selectedNodeId]);

  var memoNodeTypes = useMemo(function () { return nodeTypes; }, []);

  // ====================================================================
  // Drag da borda direita da sidebar — implementação "uncontrolled"
  // durante o arraste: mutamos `ref.current.style.width` direto no DOM
  // a cada mousemove, sem re-render do React. Só fazemos setState +
  // localStorage no mouseup. Isso evita travar o react-flow com dezenas
  // de re-renders por segundo.
  // ====================================================================
  var onSidebarDragMove = useCallback(function (e) {
    if (!draggingRef.current || !sidebarRef.current) return;
    var delta = e.clientX - dragStartRef.current.x;
    var next = dragStartRef.current.w + delta;
    if (next < SIDEBAR_MIN_WIDTH) next = SIDEBAR_MIN_WIDTH;
    if (next > SIDEBAR_MAX_WIDTH) next = SIDEBAR_MAX_WIDTH;
    sidebarRef.current.style.width = next + "px";
  }, []);

  var onSidebarDragEnd = useCallback(function () {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
    window.removeEventListener("mousemove", onSidebarDragMove);
    window.removeEventListener("mouseup", onSidebarDragEnd);
    // Lê a largura final direto do DOM e sincroniza state + localStorage.
    if (sidebarRef.current) {
      var finalWidth = sidebarRef.current.offsetWidth;
      setSidebarWidth(finalWidth);
      try { localStorage.setItem(SIDEBAR_STORAGE_KEY, String(finalWidth)); } catch (e) { /* quota etc */ }
    }
  }, [onSidebarDragMove]);

  var onSidebarDragStart = useCallback(function (e) {
    if (!sidebarRef.current) return;
    e.preventDefault();
    draggingRef.current = true;
    dragStartRef.current = {
      x: e.clientX,
      w: sidebarRef.current.offsetWidth,
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    window.addEventListener("mousemove", onSidebarDragMove);
    window.addEventListener("mouseup", onSidebarDragEnd);
  }, [onSidebarDragMove, onSidebarDragEnd]);

  var onSidebarDoubleClick = useCallback(function () {
    if (sidebarRef.current) sidebarRef.current.style.width = SIDEBAR_DEFAULT_WIDTH + "px";
    setSidebarWidth(SIDEBAR_DEFAULT_WIDTH);
    try { localStorage.setItem(SIDEBAR_STORAGE_KEY, String(SIDEBAR_DEFAULT_WIDTH)); } catch (e) { /* */ }
  }, []);

  return (
    <div className={classes.root}>
      {/* Topbar */}
      <div className={classes.topbar}>
        <Tooltip title="Voltar">
          <IconButton
            size="small"
            className={classes.backBtn}
            onClick={function () {
              // Quem veio da página de resultados ("Editar fluxo") volta para ela.
              var cameFromResults = history.location.state && history.location.state.from === "results";
              guard.leave(function () { history.push(cameFromResults ? "/fluxo/" + id + "/resultados" : "/fluxo"); });
            }}
          >
            <ArrowLeft size={18} />
          </IconButton>
        </Tooltip>

        <div className={classes.divider} />

        <input
          className={classes.titleInput}
          value={title}
          readOnly={!canEdit}
          onChange={function (e) {
            setTitle(e.target.value);
            markDirty();
          }}
          placeholder="Nome do fluxo..."
        />

        <div className={classes.topbarRight}>
          <span
            className={classes.statusTag + " " + (isActive ? classes.activeTag : classes.inactiveTag)}
          >
            <Power size={10} />
            {isActive ? "Ativo" : "Inativo"}
          </span>

          {canEdit ? (
            <React.Fragment>
              <Switch
                size="small"
                checked={isActive}
                onChange={handleToggleActive}
                color="primary"
              />

              <div className={classes.divider} />

              <button
                className={classes.saveBtn + (saved ? " " + classes.savedBtn : "")}
                onClick={handleManualSave}
                disabled={saving || (!dirty && !saved)}
              >
                {saving ? (
                  <CircularProgress size={14} style={{ color: "#fff" }} />
                ) : saved ? (
                  <React.Fragment>
                    <Check size={14} />
                    Salvo
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <Save size={14} />
                    Salvar
                    {dirty && (
                      <span
                        title="Alterações não salvas"
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          background: "#facc15",
                          marginLeft: 1,
                          flexShrink: 0,
                          boxShadow: "0 0 0 2px rgba(250,204,21,0.3)",
                        }}
                      />
                    )}
                  </React.Fragment>
                )}
              </button>
            </React.Fragment>
          ) : (
            <span className={classes.readOnlyTag}>Somente leitura</span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className={classes.body}>
        {/* Botão "+" flutuante — abre o painel de nós (escondido quando aberto) */}
        {canEdit && !paletteOpen && (
          <div
            ref={paletteToggleRef}
            className={classes.paletteToggle}
            onClick={function () { setPaletteOpen(true); }}
            title="Adicionar nó"
          >
            <Plus size={20} />
          </div>
        )}

        {/* Painel de nós — overlay flutuante que desliza da esquerda. Sem botão
            de fechar: fecha ao clicar fora (ou ESC). Fechado por padrão. */}
        <div
          ref={function (el) { paletteRef.current = el; sidebarRef.current = el; }}
          className={classes.palettePanel + (paletteOpen ? " " + classes.palettePanelOpen : "")}
          style={{ width: sidebarWidth }}
        >
          {/* Handle de resize na borda direita (arraste p/ alargar o painel) */}
          <div
            className={classes.paletteResizeHandle}
            onMouseDown={onSidebarDragStart}
            onDoubleClick={onSidebarDoubleClick}
            title="Arraste para redimensionar. Duplo clique para restaurar."
          />
          <div className={classes.sidebarList}>
            {SIDEBAR_CATEGORIES.map(function (cat) {
              return (
                <React.Fragment key={cat.label}>
                  <div className={classes.sidebarTitle}>{cat.label}</div>
                  {cat.items.filter(function (cfg) { return cfg.type !== "ai_triage" || triageEnabled; }).map(function (cfg) {
                    var Icon = cfg.icon;
                    var iconColor = NODE_ICON_COLOR(isDark);
                    var chipBg = NODE_ICON_CHIP(isDark);
                    return (
                      <div
                        key={cfg.type}
                        className={classes.nodeType}
                        draggable
                        onDragStart={function (e) { onDragStart(e, cfg.type); }}
                      >
                        <div
                          className={classes.nodeTypeIcon}
                          style={{ background: chipBg }}
                        >
                          <Icon size={14} color={iconColor} strokeWidth={2} />
                        </div>
                        <div className={classes.nodeTypeInfo}>
                          <div className={classes.nodeTypeLabel}>{cfg.label}</div>
                          <div className={classes.nodeTypeDesc}>{cfg.desc}</div>
                        </div>
                        <GripVertical
                          size={12}
                          className={classes.nodeTypeGrip}
                        />
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Canvas */}
        <div
          className={classes.canvas}
          ref={reactFlowWrapper}
          style={{ "--log-panel-offset": ((logPanelOpen ? 220 : 36) + 12) + "px" }}
        >
          {loading && (
            <div className={classes.loadingOverlay}>
              <CircularProgress size={28} style={{ color: "#5B7CFF" }} />
            </div>
          )}
          <AgentOptionsContext.Provider value={agentOptions}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={handleNodesChange}
              onEdgesChange={handleEdgesChange}
              onConnect={onConnect}
              onDrop={canEdit ? onDrop : undefined}
              nodesDraggable={canEdit}
              nodesConnectable={canEdit}
              onDragOver={onDragOver}
              onNodeDoubleClick={onNodeDoubleClick}
              onNodeDragStart={takeSnapshot}
              onNodeDrag={onNodeDrag}
              onPaneClick={onPaneClick}
              nodeTypes={memoNodeTypes}
              fitView
              fitViewOptions={{ padding: 0.3 }}
              defaultEdgeOptions={{
                type: "smoothstep",
                animated: true,
                markerEnd: { type: MarkerType.ArrowClosed, color: "#5B7CFF" },
                style: { stroke: "#5B7CFF", strokeWidth: 2 },
              }}
              deleteKeyCode={canEdit ? ["Backspace", "Delete"] : null}
            >
              <Background
                variant="dots"
                gap={20}
                size={1}
                color="rgba(91, 124, 255, 0.12)"
              />
              <Controls showInteractive={false} />
              <MiniMap
                nodeStrokeWidth={3}
                nodeColor="#5B7CFF"
                nodeBorderRadius={8}
                maskColor="rgba(0, 0, 0, 0.4)"
                zoomable
                pannable
                style={{ width: 140, height: 100, backgroundColor: "#0e1017" }}
              />
            </ReactFlow>
          </AgentOptionsContext.Provider>
        </div>

        {/* Config Panel (right) */}
        <ConfigPanel
          classes={classes}
          readOnly={!canEdit}
          selectedNode={selectedNode}
          setSelectedNodeId={setSelectedNodeId}
          updateNodeData={updateNodeData}
          queues={queues}
          whatsapps={whatsapps}
          setPromptModalOpen={setPromptModalOpen}
          fileUploading={fileUploading}
          setFileUploading={setFileUploading}
          pendingFileName={pendingFileName}
          setPendingFileName={setPendingFileName}
          pendingFile={pendingFile}
          setPendingFile={setPendingFile}
          toastError={toastError}
          api={api}
          memName={memName}
          setMemName={setMemName}
          memContent={memContent}
          setMemContent={setMemContent}
          memSummary={memSummary}
          setMemSummary={setMemSummary}
          memGenerating={memGenerating}
          setMemGenerating={setMemGenerating}
          memEditingId={memEditingId}
          setMemEditingId={setMemEditingId}
          memExpandedId={memExpandedId}
          setMemExpandedId={setMemExpandedId}
          leadStatuses={leadStatuses}
          setLeadStatuses={setLeadStatuses}
          crmBoards={crmBoards}
          crmFieldName={crmFieldName}
          setCrmFieldName={setCrmFieldName}
          memPromptModalOpen={memPromptModalOpen}
          setMemPromptModalOpen={setMemPromptModalOpen}
          edges={edges}
          nodes={nodes}
          bulkTemplates={bulkTemplates}
          loadingTemplates={loadingTemplates}
          bulkSending={bulkSending}
          bulkProgress={bulkProgress}
          handleBulkSend={handleBulkSend}
          workflowId={id}
          users={users}
          wooIntegrations={wooIntegrations}
          orderSources={orderSources}
          reloadOrderSources={loadOrderSources}
          dirty={dirty}
          agentOptions={agentOptions}
          openAgentManager={openAgentManager}
          convertNodeToAgent={convertNodeToAgent}
        />
      </div>

      {/* "Gerenciar Agentes" aberto pelo bloco "Agente IA": no agente dele ou num novo. */}
      {agentManager && (
        <AgentManager
          key={agentManager.key}
          open={agentManager.open}
          initialAgentId={agentManager.agentId}
          onAgentCreated={agentManager.agentId === null ? function (agentId) { chooseCreatedAgent(agentManager.nodeId, agentId); } : undefined}
          onClose={function () {
            setAgentManager(function (cur) { return Object.assign({}, cur, { open: false }); });
            loadAgentOptions();
          }}
        />
      )}

      {/* Log Panel — largura cheia (paleta agora é overlay flutuante) */}
      <LogPanel
        classes={classes}
        logPanelOpen={logPanelOpen}
        setLogPanelOpen={setLogPanelOpen}
        executionLogs={executionLogs}
        nodes={nodes}
        leftOffset={0}
        panelRef={logPanelRef}
      />

      {/* Prompt expand modal */}
      {promptModalOpen && (
        <PromptModal
          classes={classes}
          selectedNode={selectedNode}
          edges={edges}
          nodes={nodes}
          updateNodeData={updateNodeData}
          setPromptModalOpen={setPromptModalOpen}
        />
      )}

      {/* Memory content expand modal */}
      {memPromptModalOpen && (
        <MemoryPromptModal
          classes={classes}
          selectedNode={selectedNode}
          edges={edges}
          nodes={nodes}
          memContent={memContent}
          setMemContent={setMemContent}
          setMemPromptModalOpen={setMemPromptModalOpen}
        />
      )}

      {/* Modal "alterações não salvas" ao tentar sair. Enquanto o "Salvar e sair"
          grava, fica travado: a saída escolhida não muda no meio da gravação. */}
      {guard.confirmOpen && (
        <div
          onClick={function () { if (!saving) guard.cancel(); }}
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 2000,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
            background: "rgba(0,0,0,0.5)",
          }}
        >
          <div
            onClick={function (e) { e.stopPropagation(); }}
            style={{
              width: "min(460px, 94vw)",
              background: isDark ? "#141824" : "#ffffff",
              border: "1px solid " + (isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"),
              borderRadius: 14,
              boxShadow: "0 24px 64px rgba(0,0,0,0.45)",
              padding: 22,
              color: isDark ? "#f0f2f5" : "#111318",
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Alterações não salvas</div>
            <div style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 20, color: isDark ? "rgba(255,255,255,0.7)" : "rgba(20,22,30,0.7)" }}>
              Você tem alterações não salvas neste fluxo. Se sair agora sem salvar, elas serão perdidas.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                onClick={guard.cancel}
                disabled={saving}
                style={{
                  padding: "9px 16px", borderRadius: 9, fontSize: 13, fontWeight: 600,
                  cursor: saving ? "default" : "pointer", opacity: saving ? 0.6 : 1,
                  border: "1px solid " + (isDark ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.14)"),
                  background: "transparent", color: isDark ? "rgba(255,255,255,0.8)" : "rgba(20,22,30,0.8)",
                }}
              >
                Cancelar
              </button>
              <button
                onClick={guard.confirm}
                disabled={saving}
                style={{
                  padding: "9px 16px", borderRadius: 9, fontSize: 13, fontWeight: 600,
                  cursor: saving ? "default" : "pointer", opacity: saving ? 0.6 : 1,
                  border: "1px solid rgba(239,68,68,0.5)", background: "rgba(239,68,68,0.1)", color: "#ef4444",
                }}
              >
                Descartar e sair
              </button>
              <button
                onClick={handleSaveExit}
                disabled={saving}
                style={{
                  padding: "9px 16px", borderRadius: 9, fontSize: 13, fontWeight: 700,
                  cursor: saving ? "default" : "pointer",
                  border: "none", background: "#5B7CFF", color: "#fff", opacity: saving ? 0.6 : 1,
                }}
              >
                {saving ? "Salvando…" : "Salvar e sair"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Wrapper with Provider ──────────────────────────── */
var FlowBuilder = function () {
  return (
    <ReactFlowProvider>
      <FlowBuilderInner />
    </ReactFlowProvider>
  );
};

export default FlowBuilder;
